import _ 							from "lodash";

import Broadcast 					from "./../../Libs/Broadcast";

import { Server } 					from "./Server";
import { Settings } 				from "./Settings";
import { Version } 					from "./Version";
import { Layout } 					from "./Layout";
import { Elements } 				from "./Elements";
import { ScreenService } 			from "./Screen";
import { Metadata } 				from "./Metadata";
import { Notifications } 			from "./Notifications";
import { User } 					from "./User";

// MIME-тип полезной нагрузки перетаскивания ассета (список ассетов, ИИ-чат) во фрейм превью.
// По типу в dataTransfer дашборд понимает, что перетаскивается именно ассет, ещё до сброса (getData во время drag недоступен).
export const FRAME_DROP_MIME_TYPE = 'application/x-mraidio-frame-asset';
// Началом перетаскивания считается зажатие и смещение мыши не менее чем на это количество пикселей
export const FRAME_DROP_DRAG_THRESHOLD = 20;
// Минимальный масштаб перетащенного объекта на сцене
export const FRAME_DROP_MIN_SCALE = 0.1;
// Задержка перед выделением созданного перетаскиванием элемента: превью включает редактор
// после переключения вкладки (Renderer.activateSceneEditor с задержкой 200мс) - выделение, отправленное раньше, будет проигнорировано
export const FRAME_DROP_EDITOR_SELECT_DELAY = 250;
// Типы креативов, у которых в панели настроек есть вкладка редактора (дерево элементов, см. SettingsContent.vue).
// У остальных креативов (например, playable) редактора в дашборде нет - переключать на него нечего
const FRAME_DROP_EDITOR_CREATIVE_TYPES = ['end-card', 'banner', 'storyboard', 'editable-playable'];

// Тип элемента, который создаётся на сцене для ассета (нет в списке - ассет нельзя разместить на сцене)
const FRAME_DROP_ELEMENT_TYPES = {
	image: 'image',
	video: 'video',
	atlas: 'sprite-animation',
	'sprite-animation': 'sprite-animation',
	spine: 'spine'
};

// Свойства элемента для каждого типа элемента (те же свойства, что передаёт меню добавления элемента EditorElementAdd.vue)
const FRAME_DROP_ELEMENT_PROPS = {
	image: assetCodename => ({image: assetCodename}),
	video: assetCodename => ({video: assetCodename}),
	spine: assetCodename => ({spine: assetCodename}),
	// Спрайтовая анимация создаётся с бесконечным повтором, а проигрывание запускается событием Created (как в EditorElementAdd.vue)
	'sprite-animation': assetCodename => ({spriteAnimation: assetCodename, loop: 'infinity'})
};

// Тип ассета креатива по расширению файла (форматы, которые принимает роут /asset/add)
const FRAME_DROP_ASSET_TYPES_BY_EXTENSION = {
	png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', svg: 'image',
	mp4: 'video',
	mp3: 'sound',
	ttf: 'web-font', otf: 'web-font', woff: 'web-font', woff2: 'web-font'
};
// Расширения картинок, из набора которых сервер собирает ассет спрайтовой анимации (/asset/add-sprite-animation)
const FRAME_DROP_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'];
// Расширения частей набора Spine (/asset/add-spine)
const FRAME_DROP_SPINE_EXTENSIONS = ['json', 'atlas', 'png'];

// Библиотека сервиса Asset: перетаскивание ассетов и файлов во фрейм превью (картинки, спрайтовые анимации, спайны, видео и шрифты).
//
// Схема работы:
// 1. Миниатюра ассета (список ассетов, сообщение ИИ-чата) помечается draggable и при начале перетаскивания кладёт в dataTransfer
//    полезную нагрузку с codename и типом ассета; файлы из файловой системы и из ИИ-чата кладутся во временное хранилище сервиса
// 2. Во время перетаскивания над фреймом показывается оверлей, а для шрифта подсвечивается текст под указателем
// 3. При сбросе над фреймом центр объекта ставится в точку сброса, а масштаб подбирается так, чтобы объект не вышел за границы сцены
//    (но не менее 10%). Файлы сначала создаются как ассеты (без подтверждения, в отличие от ИИ-чата), затем создаётся элемент.
//    Ассеты звуков просто добавляются в ассеты - элемент для них не создаётся
export const AssetFrameDrop = {

	Name: "Asset Frame Drop",

	Events: {

		// Перетаскивание закончилось (сброс или отмена) - сохранённая нагрузка больше не нужна
		"Frame Drop Finished": function() {

			this.clearFrameDropDragPayload();

		},

		// В хранилище версии появился новый элемент (например, ассет перетащили во фрейм) - если он создан
		// перетаскиванием и настройка профиля включена, переключаемся на редактор и выделяем элемент
		"Selected Version Element Added": function(element) {

			this.focusFrameDropCreatedElement(element);

		}

	},

	init() {

		// Временное хранилище файлов перетаскивания: файл нельзя сериализовать в dataTransfer, поэтому в нагрузке хранится только ключ
		this.frameDropFiles = {};
		this.frameDropFilesId = 0;

		// Полезная нагрузка текущего перетаскивания (во время drag содержимое dataTransfer недоступно - getData возвращает пустую строку)
		this.frameDropDragPayload = null;

		// Создание элемента перетаскиванием: ждём от сервера появления элемента, чтобы переключиться на редактор и выделить его
		this.frameDropFocusRequest = null;

	},

	getFrameDropMimeType() {

		return FRAME_DROP_MIME_TYPE;

	},

	getFrameDropDragThreshold() {

		return FRAME_DROP_DRAG_THRESHOLD;

	},

	getFrameDropMinScale() {

		return FRAME_DROP_MIN_SCALE;

	},

	// --- Полезная нагрузка перетаскивания ---

	// Полезная нагрузка для ассета креатива: ассет уже создан, при сбросе создаётся только элемент
	createFrameDropAssetPayload(asset) {

		if (!asset || !asset.codename) return null;
		// Ассеты, из которых нельзя создать элемент (звуки, данные), и атласы без пометки спрайтовой анимации не перетаскиваются
		if (!FRAME_DROP_ELEMENT_TYPES[asset.type] && asset.type !== 'web-font') return null;
		if (asset.type === 'atlas' && !asset.options?.['spritesAnimation']) return null;

		return {
			kind: 'asset',
			assetType: asset.type,
			assetCodename: asset.codename,
			title: asset.title || asset.codename
		};

	},

	// Полезная нагрузка для файлов (файловая система или ИИ-чат): ассеты будут созданы при сбросе во фрейм
	createFrameDropFilesPayload(files) {

		files = _.filter(files || [], file => !!file && !!file.name);

		if (!files.length) return null;

		let storeKey = `frame-drop-files-${++this.frameDropFilesId}`;

		this.frameDropFiles[storeKey] = files;

		return {
			kind: 'files',
			storeKey,
			files: _.map(files, file => ({name: file.name, size: file.size})),
			title: files.length === 1 ? files[0].name : `${files.length} files`
		};

	},

	// Полезная нагрузка перетаскиваемых файлов для подсказки оверлея: во время драга содержимое файлов
	// браузер не отдаёт, поэтому имена берутся из списка перетаскиваемых элементов (если браузер их отдаёт)
	createFrameDropDragPreviewPayload(dataTransfer) {

		let files = _.compact(_.map(Array.from(dataTransfer?.items || []), item => {

			let file = item?.kind === 'file' ? item.getAsFile() : null;

			return file ? {name: file.name, size: file.size} : null;

		}));

		return {kind: 'files', files, title: files.length === 1 ? files[0].name : `${files.length} files`};

	},

	releaseFrameDropFiles(storeKey) {

		if (storeKey) delete this.frameDropFiles[storeKey];

	},

	getFrameDropFiles(storeKey) {

		return this.frameDropFiles[storeKey] || [];

	},

	// Записывает полезную нагрузку в dataTransfer перетаскивания (вызывается в dragstart миниатюры)
	writeFrameDropPayload(dataTransfer, payload) {

		if (!dataTransfer || !payload) return;

		this.frameDropDragPayload = payload;

		dataTransfer.setData(FRAME_DROP_MIME_TYPE, JSON.stringify(payload));
		// Без текстовых данных часть браузеров не разрешает перетаскивание (курсор "запрещено")
		dataTransfer.setData('text/plain', payload.title || '');

		dataTransfer.effectAllowed = 'copy';

	},

	// Полезная нагрузка из dataTransfer (содержимое доступно только при сбросе)
	readFrameDropPayload(dataTransfer) {

		let raw = null;

		try { raw = dataTransfer?.getData(FRAME_DROP_MIME_TYPE) || null; } catch (e) { raw = null; }

		if (raw) {

			try { return JSON.parse(raw); } catch (e) { /* некорректная нагрузка - используем сохранённую */ }

		}

		return this.frameDropDragPayload;

	},

	setFrameDropDragPayload(payload) {

		this.frameDropDragPayload = payload;

	},

	getFrameDropDragPayload() {

		return this.frameDropDragPayload;

	},

	clearFrameDropDragPayload() {

		this.frameDropDragPayload = null;

	},

	// Перетаскивается ассет (миниатюра списка ассетов или сообщения ИИ-чата)
	isFrameDropAssetDrag(dataTransfer) {

		return !!dataTransfer && _.includes(Array.from(dataTransfer.types || []), FRAME_DROP_MIME_TYPE);

	},

	// Перетаскиваются файлы (из файловой системы)
	isFrameDropFilesDrag(dataTransfer) {

		return !!dataTransfer && _.includes(Array.from(dataTransfer.types || []), 'Files');

	},

	// --- Описание перетаскиваемого объекта ---

	// Тип ассета полезной нагрузки (для файлов определяется по расширениям имён)
	getFrameDropPayloadAssetType(payload) {

		if (payload?.kind !== 'files') return payload?.assetType;

		// У нагрузки драга могут быть только имена файлов - содержимое файлов доступно лишь при сбросе
		let files = this.getFrameDropFiles(payload.storeKey);

		return this.getFrameDropFilesAssetType(files.length ? files : payload.files);

	},

	// Тип ассета, который будет создан из набора файлов (алгоритм совпадает с роутами /asset/add, /asset/add-spine, /asset/add-sprite-animation)
	getFrameDropFilesAssetType(files) {

		files = _.filter(files || [], file => !!file?.name);

		if (!files.length) return null;

		let extensions = _.map(files, file => this.getFrameDropFileExtension(file.name));

		// Один файл - тип ассета по расширению (png -> image, mp3 -> sound, ttf -> web-font и т.д.)
		if (files.length === 1) return FRAME_DROP_ASSET_TYPES_BY_EXTENSION[extensions[0]] || null;

		// Spine - набор из трёх файлов: данные скелета (.json), атлас (.atlas) и текстура (.png)
		if (files.length === FRAME_DROP_SPINE_EXTENSIONS.length && _.isEqual(_.sortBy(_.uniq(extensions)), _.sortBy(FRAME_DROP_SPINE_EXTENSIONS))) return 'spine';

		// Несколько картинок - сервер соберёт из них ассет спрайтовой анимации
		if (_.every(extensions, ext => _.includes(FRAME_DROP_IMAGE_EXTENSIONS, ext))) return 'sprite-animation';

		return null;

	},

	getFrameDropFileExtension(fileName) {

		let match = String(fileName || '').toLowerCase().match(/\.([a-z0-9]+)$/);

		return match ? match[1] : '';

	},

	// Тип элемента сцены, который создаётся для ассета
	getFrameDropElementType(assetType) {

		return FRAME_DROP_ELEMENT_TYPES[assetType] || null;

	},

	// Ассет - шрифт: перетаскивается на текст во фрейме, чтобы установить этому тексту перетащенный шрифт
	isFrameDropFontPayload(payload) {

		return this.getFrameDropPayloadAssetType(payload) === 'web-font';

	},

	// Ассет - звук: при перетаскивании во фрейм просто добавляется в ассеты
	isFrameDropSoundPayload(payload) {

		return this.getFrameDropPayloadAssetType(payload) === 'sound';

	},

	// --- Оверлей над фреймом ---

	// Текст подсказки оверлея над превью-фреймом
	getFrameDropOverlayText(payload) {

		if (this.isFrameDropFontPayload(payload)) return {
			icon: 'fa-font',
			title: 'Move the font onto a text in the frame',
			description: 'The font of the text under the cursor will be replaced with the dragged one.'
		};

		return {
			icon: 'fa-object-group',
			title: 'Drop images, sprite animations, spines and videos on the scene',
			description: 'The center of the object is placed at the drop point, and the scale fits the screen (not less than 10%). Sounds are just added to the assets.'
		};

	},

	// --- Геометрия сцены и расчёт размещения ---

	// Экран, на котором создаётся элемент: текущий экран дашборда, а если экран ещё не выбран
	// (например, сразу после загрузки дашборда) - первый экран версии (см. ScreenService.getCurrentScreen)
	getFrameDropScreen() {

		return ScreenService.getCurrentScreen();

	},

	// Геометрия сцены: логическая сетка сцены (root-container) и её масштаб внутри кадра превью
	getFrameDropScene() {

		let previewSize = Layout.getPreviewSize(),
			screenCodename = this.getFrameDropScreen()?.codename,
			rootContainer = screenCodename ? Elements.getVersionElementsByScreen(Version.SelectedVersion)?.[screenCodename]?.['root-container'] : null,
			orientation = Layout.getPreviewSizeOrientation(),
			props = rootContainer?.props?.[Layout.getPreviewSizeKey()] || {};

		// Логическая сетка сцены: у креативов без логической сетки сцена по размерам совпадает с кадром
		let width = props[orientation === 'landscape' ? 'widthLandscape' : 'widthPortrait'] || previewSize?.width || 0,
			height = props[orientation === 'landscape' ? 'heightLandscape' : 'heightPortrait'] || previewSize?.height || 0;

		// Сцена приводится к размеру кадра без искажений по свойству scaleStrategy: cover - покрывает кадр, fit - вписывается
		// (та же математика, что в getRootContainerScale рендерера превью)
		let scaleX = previewSize?.width ? previewSize.width / width : 1,
			scaleY = previewSize?.height ? previewSize.height / height : 1,
			scaleStrategy = props.scaleStrategy || Metadata.ElementPropertiesByCodename?.['scaleStrategy']?.default,
			scale = scaleStrategy === 'cover' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);

		return {
			width,
			height,
			scale: scale > 0 ? scale : 1,
			viewportWidth: previewSize?.width || 0,
			viewportHeight: previewSize?.height || 0
		};

	},

	// Точка сброса в координатах кадра превью (без учёта CSS-масштаба предпросмотра, который может быть изменён зумом)
	getFrameDropFramePoint(frameRect, scene, clientX, clientY) {

		let scaleX = (frameRect?.width && scene.viewportWidth) ? frameRect.width / scene.viewportWidth : 1,
			scaleY = (frameRect?.height && scene.viewportHeight) ? frameRect.height / scene.viewportHeight : 1;

		return {
			x: (clientX - (frameRect?.left || 0)) / scaleX,
			y: (clientY - (frameRect?.top || 0)) / scaleY
		};

	},

	// Размещение перетащенного объекта: центр объекта - в точке сброса, масштаб подобран так,
	// чтобы объект не вышел за границы экрана (но не менее 10%)
	computeFrameDropPlacement(frameRect, clientX, clientY, objectSize = null) {

		let scene = this.getFrameDropScene(),
			point = this.getFrameDropFramePoint(frameRect, scene, clientX, clientY);

		// Свойство position элемента задаётся относительно центра родителя (сцены) в единицах логической сетки сцены
		let position = [
			_.round((point.x - scene.viewportWidth / 2) / scene.scale, 2),
			_.round((point.y - scene.viewportHeight / 2) / scene.scale, 2)
		];

		// Масштаб: объект вписывается в сцену целиком, больше своего размера не увеличивается (и не меньше 10%)
		let fitScale = (objectSize?.width && objectSize?.height) ? Math.min(scene.width / objectSize.width, scene.height / objectSize.height) : 1,
			scale = _.clamp(Math.min(1, fitScale), FRAME_DROP_MIN_SCALE, 1);

		return {position, scale};

	},

	// Codename нового элемента: у картинки из атласа берётся имя файла внутри атласа (в ассетах версии она лежит
	// под составным codename атлас|номер|файл.png, см. Asset.getMergedAssetCodename) - так codename элемента читаемый
	// (им же помечаются картинки из атласа в сообщениях превью - см. Preview.vue)
	getFrameDropElementCodename(assetCodename) {

		let asset = this.getAsset(Version.SelectedVersion, assetCodename);

		return (asset ? Asset.getMergedAssetCodename(asset) : assetCodename).split('.')[0];

	},

	// Размер объекта на сцене: элемент рисует картинку и видео в их пикселях, поэтому размеры берём у самого файла.
	// Для Spine и спрайтовой анимации точные границы анимации неизвестны - берём размер текстуры (верхняя оценка)
	getFrameDropObjectSize(payload) {

		let asset = payload?.kind === 'asset' ? this.getAsset(Version.SelectedVersion, payload.assetCodename) : null;

		if (!asset) return Promise.resolve(null);

		// У атласа (в том числе спрайтовой анимации) размеры берутся у текстуры, а не у json атласа
		let url = this.getAssetActualUrl(asset, {imagesForAtlases: true});

		if (!url) return Promise.resolve(null);

		return asset.type === 'video' ? this.loadFrameDropVideoSize(url) : this.loadFrameDropImageSize(url);

	},

	loadFrameDropImageSize(url) {

		return new Promise(resolve => {

			let image = new Image();

			image.onload = () => resolve({width: image.naturalWidth, height: image.naturalHeight});
			image.onerror = () => resolve(null);

			image.src = url;

		});

	},

	loadFrameDropVideoSize(url) {

		return new Promise(resolve => {

			let video = document.createElement('video');

			video.preload = 'metadata';
			video.onloadedmetadata = () => resolve({width: video.videoWidth, height: video.videoHeight});
			video.onerror = () => resolve(null);

			video.src = url;

		});

	},


	// --- Создание элемента и создание ассетов из файлов ---

	// Создание элемента перетащенного ассета: центр элемента ставится в точку сброса, масштаб - подобранный под границы сцены
	createFrameDropElement(payload, placement) {

		let elementCodename = this.getFrameDropElementCodename(payload.assetCodename),
			elementType = this.getFrameDropElementType(payload.assetType),
			elementProps = FRAME_DROP_ELEMENT_PROPS[elementType]?.(payload.assetCodename);

		if (!elementType || !elementProps) return false;

		let screen = this.getFrameDropScreen();

		// Экраны версии ещё не загружены - создавать элемент не на чем
		if (!screen) {

			Notifications.showError('The screens of the version are not loaded yet - drop the asset again in a moment');

			return false;

		}

		// Сервер ответит событием "Selected Version Element Added" - тогда переключимся на редактор и выделим элемент
		this.frameDropFocusRequest = {versionId: Version.SelectedVersion.id, screenCodename: screen.codename, elementType};

		Server.sendToServer({
			CreateElement: {
				versionId: Version.SelectedVersion.id,
				screenId: screen.id,
				elementId: Elements.SelectedElement?.id || 0,
				elementCodename: elementType,
				newElementCodename: elementCodename,
				newElementProps: {
					...elementProps,
					// Позиция и масштаб задаются при создании - элемент сразу появляется в точке сброса
					position: placement.position,
					// Свойство scale элемента - массив (ось X и ось Y), масштаб объекта одинаков по обеим осям
					scale: [placement.scale, placement.scale]
				}
			}
		});

		return true;

	},

	// Перетащенный ассет креатива размещается на сцене (ассет уже создан - создаём элемент)
	placeFrameDropAsset(payload, placement) {

		if (!payload?.assetCodename || !this.getFrameDropElementType(payload.assetType)) return false;

		// Картинка из атласа в превью дашборда по умолчанию не выгружается - отправляем её перед созданием элемента
		this.sendFrameDropAssetToPreview(payload.assetCodename);

		return this.createFrameDropElement(payload, placement);

	},

	// Картинка внутри атласа: в ассетах версии и в релизах она лежит под составным codename (атлас|номер|файл.png),
	// а в превью дашборда картинки из атласов по умолчанию не выгружаются. Отправляем превью саму картинку под тем же
	// codename (как дашборд делает при изменении ассетов, см. Preview.vue), чтобы созданный элемент сразу отрисовался
	sendFrameDropAssetToPreview(assetCodename) {

		let asset = this.getAsset(Version.SelectedVersion, assetCodename);

		if (asset?.type !== 'image' || !asset.options?.atlases?.length) return;

		Broadcast.call("Send To iFrame", [{
			setAssets: {
				[asset.codename]: Asset.cleanVersionAsset(asset, true)
			}
		}]);

	},

	// Перетащенные файлы: ассеты создаются автоматически (без подтверждения, в отличие от ИИ-чата), затем создаётся элемент.
	// Ассеты звуков просто добавляются в ассеты - элемент для них не создаётся.
	// dropInfo - {frameRect, clientX, clientY, element} - геометрия сброса и элемент под указателем (для шрифта)
	async importFrameDropFiles(files, dropInfo) {

		files = _.filter(files || [], file => !!file?.name);

		if (!files.length) return false;

		let assetType = this.getFrameDropFilesAssetType(files);

		if (!assetType) {

			Notifications.showError(`These files can't be placed on the scene: ${_.map(files, file => file.name).join(', ')}`);

			return false;

		}

		let assetCodename = await this.createFrameDropAsset(files, assetType);

		if (!assetCodename) return false;

		// Звук: перетаскивание во фрейм просто добавляет ассет в ассеты
		if (assetType === 'sound') {

			Notifications.showSuccess(`Sound asset "${assetCodename}" was added to the assets`);

			return true;

		}

		// Шрифт: если файл сброшен на текст - шрифт этого текста заменяется на перетащенный
		if (assetType === 'web-font') return this.applyFrameDropFont(assetCodename, dropInfo?.element);

		let objectSize = await this.getFrameDropObjectSize({kind: 'asset', assetType, assetCodename});

		this.createFrameDropElement({assetType, assetCodename}, this.computeFrameDropPlacement(dropInfo.frameRect, dropInfo.clientX, dropInfo.clientY, objectSize));

		return true;

	},

	// Создание ассета из перетащенных файлов: возвращает codename созданного ассета.
	// Сервер присылает новый ассет событием "Selected Version Assets Changed" (тот же механизм, что у загрузки нового ассета в списке ассетов)
	createFrameDropAsset(files, assetType) {

		let versionId = Version.SelectedVersion.id,
			timeout = null;

		let created = new Promise(resolve => {

			// Broadcast.call разворачивает массив аргументов, поэтому пары [versionId, assetCodename] приходят
			// отдельными аргументами (см. Localization.js: обработчик получает versionId, locale, codename).
			// Собираем их rest-параметром, иначе первым аргументом придёт только первая пара
			Broadcast.once("Selected Version Assets Changed", (...changedAssets) => {

				let changed = _.find(changedAssets, item => item?.[0] === versionId);

				clearTimeout(timeout);

				resolve(changed?.[1] || null);

			}, "Frame Drop Asset Created");

			// Ассет не появился (ошибка загрузки на сервере) - не держим перетаскивание бесконечно
			timeout = setTimeout(() => {

				Broadcast.off("Selected Version Assets Changed", "Frame Drop Asset Created");

				resolve(null);

			}, 30000);

		});

		let started = false;

		if (assetType === 'spine') started = this.addSpineAsset({type: assetType}, files);
		else if (assetType === 'sprite-animation') started = this.addSpriteAnimationAsset({type: assetType}, files);
		else if (files[0]) {

			this.addAsset({type: assetType}, files[0]);

			started = true;

		}

		if (!started) return Promise.resolve(null);

		return created;

	},

	// --- Шрифт ---

	// Применение перетащенного шрифта к тексту: у текстового элемента хранится codename сообщения локализации,
	// а сам шрифт задаётся стилем fontFamily этого сообщения
	applyFrameDropFont(fontAssetCodename, element) {

		let textCodename = this.getFrameDropTextCodename(element);

		if (!fontAssetCodename) return false;

		if (!textCodename) {

			Notifications.showError('The font can be applied to a text only - drop it onto a text in the frame');

			return false;

		}

		Server.sendToServer({
			ChangeLocalizationStyle: {
				versionId: Version.SelectedVersion.id,
				// Шрифт применяется к локали превью (в дашборде по умолчанию редактируется локаль en)
				locale: this.getFrameDropLocale(),
				codename: textCodename,
				styleName: 'fontFamily',
				styleValue: fontAssetCodename
			}
		});

		return true;

	},

	// Codename сообщения локализации текстового элемента для текущего размера превью
	getFrameDropTextCodename(element) {

		if (element?.type !== 'text') return null;

		return element.props?.[Layout.getPreviewSizeKey()]?.text || null;

	},

	getFrameDropLocale() {

		let locale = Settings.getVersionSettingValue(Version.SelectedVersion.id, 'locale');

		// Локаль "auto" определяется устройством пользователя - в дашборде такой текст редактируется в локали en
		return (!locale || locale === 'auto') ? 'en' : locale;

	},

	// --- Фокус на созданном элементе ---

	// Включено ли в настройках профиля переключение на редактор после создания элемента перетаскиванием.
	// По умолчанию включено: настройка выключается только явно (значение false)
	isFrameDropOpenEditorEnabled() {

		return User.currentUser?.options?.['features']?.['editor-open-on-frame-drop'] !== false;

	},

	// Новый элемент появился в хранилище версии: если он создан перетаскиванием ассета во фрейм и настройка
	// профиля включена - переключаемся на вкладку редактора (дерево элементов) и выделяем элемент
	focusFrameDropCreatedElement(element) {

		let request = this.frameDropFocusRequest;

		if (!request || !element) return;
		if (element['version_id'] !== request.versionId || element['screen_codename'] !== request.screenCodename) return;
		if (element.type !== request.elementType) return;

		this.frameDropFocusRequest = null;

		if (!this.isFrameDropOpenEditorEnabled()) return;
		// Вкладка редактора есть не у всех креативов (см. FRAME_DROP_EDITOR_CREATIVE_TYPES) - иначе откроется пустая панель
		if (!_.includes(FRAME_DROP_EDITOR_CREATIVE_TYPES, Creative.SelectedCreative?.type)) return;

		this.openFrameDropEditor();

		// Редактор в превью включается после переключения вкладки - выделение отправляем после этого,
		// иначе превью проигнорирует выделение (редактор ещё не активен)
		setTimeout(() => {

			let screenElements = Elements.getVersionElementsByScreen(Version.SelectedVersion)?.[request.screenCodename] || {};
			let createdElement = screenElements[element.codename] || element;

			Elements.selectElement(createdElement);

			Broadcast.call("Send To iFrame", [{
				SelectElement: [request.screenCodename, element.codename]
			}]);

		}, FRAME_DROP_EDITOR_SELECT_DELAY);

	},

	// Открытие вкладки редактора: панель настроек открывается, если была закрыта, и переключается
	// на вкладку с деревом элементов (Renderer включает редактор в превью по событию смены вкладки)
	openFrameDropEditor() {

		if (!Layout.asideOpened) {

			Layout.toggleAside('settings');

		} else if (Layout.asideType !== 'settings') {

			Layout.asideType = 'settings';
			Broadcast.call('Aside Type Changed');

		}

		Layout.setSettingsTab('elements');

	}

};


